import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useLanguage } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import PropertyCard from "@/components/PropertyCard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Account() {
  const { t } = useLanguage();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user === null) {
      navigate("/login");
      return;
    }
    if (!user) return; // still loading
    axios
      .get(`${API}/me/favorites`, { withCredentials: true })
      .then((r) => setItems(r.data))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [user, navigate]);

  if (!user) {
    return (
      <div className="pt-40 pb-24 max-w-md mx-auto px-6">
        <div className="animate-pulse h-6 w-1/2 bg-line" />
      </div>
    );
  }

  return (
    <div className="pt-32 pb-24 min-h-screen">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="grid md:grid-cols-12 gap-10 mb-16 items-end">
          <div className="md:col-span-8">
            <div className="overline mb-3">{t("auth.my_account")}</div>
            <h1 className="font-serif text-5xl md:text-6xl leading-tight text-ink font-light">
              {t("auth.hello")}, <span className="italic">{user.name}</span>.
            </h1>
            <div className="mt-4 text-muted text-sm space-y-0.5">
              <div>{user.email}</div>
              {user.phone && <div>{user.phone}</div>}
            </div>
          </div>
          <div className="md:col-span-4 md:text-right">
            <button onClick={() => { logout(); toast.success("Signed out"); navigate("/"); }}
              data-testid="account-logout"
              className="text-xs uppercase tracking-widest2 underline underline-offset-8 hover:text-accent">
              {t("auth.sign_out")}
            </button>
          </div>
        </div>

        <div className="border-t border-line pt-10">
          <div className="overline mb-3">{t("auth.favorites")}</div>
          <h2 className="font-serif text-3xl md:text-4xl leading-tight text-ink font-light">
            {items.length} {items.length === 1 ? t("auth.prop_singular") : t("auth.prop_plural")}
          </h2>
        </div>

        {loading ? (
          <div className="mt-12 grid md:grid-cols-3 gap-8">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="animate-pulse aspect-[4/5] bg-line" />
            ))}
          </div>
        ) : items.length === 0 ? (
          <div className="mt-16 text-center text-muted">
            <p className="text-lg">{t("auth.empty_favorites")}</p>
            <Link to="/properties" className="mt-6 inline-block text-sm underline underline-offset-8 hover:text-accent">
              {t("nav.properties")} →
            </Link>
          </div>
        ) : (
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {items.map((p, i) => (
              <PropertyCard key={p.id} property={p} index={i} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
